/*
 * ATTENTION: This file is Good Code. Before you edit it, ask yourself:
 *
 * • Does this have any functionality that's unrelated or indirectly related to content?
 * • Does the only likely use case exist in the module I'm working on right now?
 * • Could I add this to another, more appropriate module?
 * • Am I repeating code that's already here?
 *
 * If you answered 'yes' to any of these, stop and think really hard if you
 * actually need to do whatever it is you're about to do.
 * <3 -- NS
 */
(function () {
  'use strict'

  /**
   * @ngdoc service
   * @name ThismomentContentCore
   * @module thismoment.content.core
   *
   * @description
   * Use the content service to hold content objects for use between various
   * frontend components.
   *
   * Content is stored in a keyed object to facilitate storing different
   * sets of content for different uses throughout the application.
   *
   * <code>
   * var contentLists = {};
   * Content.addContent('playlist', item);
   *
   * console.log(Content.getContents('playlist'));
   * [{
   *   // content item
   * },{
   *   // content item
   * },{
   *   // content item
   * },{
   *   // content item
   * }]
   * </code>
   */

  var MODULE_NAME = 'thismoment.content.core'

  angular
    .module(MODULE_NAME, [])
    .provider('Content', contentProvider)

  function contentProvider () {
    return {
      $get: contentService
    }
  }

  // TODO: Add error handling for key not found.
  function contentService () {
    var contentLists = {}

    var ContentService = {
      addContent: addContent,
      addContentAt: addContentAt,
      addContents: addContents,
      addContentToFront: addContentToFront,
      addContentsToFront: addContentsToFront,
      emptyContents: emptyContents,
      filterDuplicates: filterDuplicates,
      findContent: findContent,
      findContents: findContents,
      getContentCount: getContentCount,
      getContents: getContents,
      removeContent: removeContent
    }

    return ContentService

    /**
     * @ngdoc function
     * @name addContent
     *
     * @param {string} key Location of added content.
     * @param {object} content Content object
     *
     * @description
     * Adds a piece of content to the content list of a specified key
     */
    function addContent (key, content) {
      _checkForKey(key)
      if (!contentLists[key].includes(content)) {
        contentLists[key].push(content)
      }
    }

    /**
     * @ngdoc function
     * @name addContentAt
     *
     * @param {string} key Location of added content.
     * @param {integer} index Position of added content.
     * @param {object} content Content object
     *
     * @description
     * Adds a piece of content to the content list of a specified key
     */
    function addContentAt (key, index, content) {
      _checkForKey(key)
      contentLists[key].splice(index, 0, content)
    }

    /**
     * @ngdoc function
     * @name addContents
     *
     * @param {string} key Location of added content.
     * @param {array} contents Array of content objects.
     *
     * @description
     * Adds pieces of content to the content list of a specified key
     */
    function addContents (key, contents) {
      angular.forEach(contents, function (content) {
        addContent(key, content)
      })
    }

    /**
     * @ngdoc function
     * @name addContentToFront
     *
     * @param {string} key Location of added content.
     * @param {object} content a content object from the API
     *
     * @description
     * Add content to the front of a specified content list.
     */
    function addContentToFront (key, content) {
      _checkForKey(key)
      contentLists[key].unshift(content)
    }

    /**
     * @ngdoc function
     * @name addContentsToFront
     *
     * @param {string} key Location of added content.
     * @param {array} contents an array of content objects from the API
     *
     * @description
     * Adds pieces of content to the front of content list
     */
    function addContentsToFront (key, contents) {
      angular.forEach(contents.reverse(), function (content) {
        addContentToFront(key, content)
      })
    }

    /**
     * @ngdoc function
     * @name removeContent
     *
     * @param {string} key Key to emtpty.
     *
     * @description
     * Clears all content from a given key. Leave blank to empty all keys.
     */
    function emptyContents (key) {
      if (angular.isUndefined(key)) {
        contentLists = {}
      } else {
        contentLists[key] = []
      }
    }

    /**
     * @ngdoc function
     * @name filterDuplicates
     *
     * @param {string} key Location of added content.
     * @param {int} id the id of the piece of content you are trying to find
     *
     * @description
     * Finds a piece of content by its key and id
     */
    function filterDuplicates (key, items) {
      _checkForKey(key)
      return items.filter(function (item) {
        return !findContent(key, item.id)
      })
    }

    /**
     * @ngdoc function
     * @name findContent
     *
     * @param {string} key Location of added content.
     * @param {int} id the id of the piece of content you are trying to find
     *
     * @description
     * Finds a piece of content by its key and id
     */
    function findContent (key, id) {
      _checkForKey(key)
      var items = contentLists[key].filter(function (content) {
        return parseInt(content.id) === parseInt(id)
      })

      return items.length ? items[0] : null
    }

    /**
     * @ngdoc function
     * @name findContents
     *
     * @param {string} key Location of added content.
     * @param {array} ids Ids of content to search for.
     *
     * @description
     * Find multiple pieces of content in a given key
     */
    function findContents (key, ids) {
      var items = []

      angular.forEach(contentLists[key], function (content) {
        var item = findContent(key, content.id)

        if (item) {
          items.push(item)
        }
      })

      return items
    }

    /**
     * @ngdoc function
     * @name getContentCount
     *
     * @param {string} key Content location to count.
     *
     * @description
     * Return the count of contents stored under a given key
     */
    function getContentCount (key) {
      return angular.isDefined(contentLists[key]) ? contentLists[key].length : 0
    }

    /**
     * @ngdoc function
     * @name getContents
     *
     * @param {string} key Location of added content.
     *
     * @description
     * Return the contents listed under a given key
     */
    function getContents (key) {
      return contentLists[key] || []
    }

    /**
     * @ngdoc function
     * @name removeContent
     *
     * @param {string} key Location of added content.
     * @param {int} id Id of content to be removed
     *
     * @description
     *
     */
    function removeContent (key, id) {
      var idx = contentLists[key].findIndex(function (content) {
        return parseInt(content.id, 10) === parseInt(id, 10)
      })

      return !!(contentLists[key].splice(idx, 1).length)
    }

    function _checkForKey (key) {
      if (angular.isUndefined(contentLists[key])) {
        contentLists[key] = []
      }
    }
  }

  module.exports = MODULE_NAME
})()



// WEBPACK FOOTER //
// ./src/content/content.js